'use client';

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Mails, FileSearch, Settings, LogOut, Fingerprint, Wrench, Users, Newspaper, CircleHelp as HelpCircle, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { createSupabaseBrowserClient } from '@/lib/supabase/client';

const navGroups = [
  {
    label: 'General',
    items: [
      { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Contenido',
    items: [
      { href: '/admin/servicios', label: 'Servicios', icon: Wrench },
      { href: '/admin/equipo', label: 'Equipo', icon: Users },
      { href: '/admin/noticias', label: 'Noticias', icon: Newspaper },
      { href: '/admin/preguntas', label: 'FAQs', icon: HelpCircle },
    ],
  },
  {
    label: 'Bandeja',
    items: [
      { href: '/admin/contactos', label: 'Contactos', icon: Mails },
      { href: '/admin/pericias', label: 'Pericias', icon: FileSearch },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { href: '/admin/ajustes', label: 'Ajustes', icon: Settings },
    ],
  },
];

export function AdminSidebar({ user }: { user: { email: string; full_name: string; role: string } }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  async function handleSignOut() {
    const supabase = createSupabaseBrowserClient();
    await supabase.auth.signOut();
    navigate('/admin/login');
  }

  const sidebar = (
    <>
      <div className="border-b border-border p-5">
        <Link to="/admin" className="flex items-center gap-2.5" onClick={() => setMobileOpen(false)}>
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
            <Fingerprint className="h-5 w-5" />
          </span>
          <div className="leading-tight">
            <p className="font-serif text-base font-bold text-primary">VPS Admin</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">CMS Panel</p>
          </div>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto p-3">
        {navGroups.map((group) => (
          <div key={group.label} className="mb-4">
            <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {group.label}
            </p>
            {group.items.map((item) => {
              const Icon = item.icon;
              const active =
                (item.href === '/admin' && pathname === '/admin') ||
                (item.href !== '/admin' && pathname?.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground/70 hover:bg-secondary hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-4">
        <p className="text-xs font-semibold text-primary">{user.full_name || user.email}</p>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{user.role}</p>
        <button
          type="button"
          onClick={handleSignOut}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground/70 transition hover:bg-secondary"
        >
          <LogOut className="h-4 w-4" />
          Salir
        </button>
      </div>
    </>
  );

  return (
    <>
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed left-4 top-4 z-50 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card shadow-sm lg:hidden"
        aria-label="Menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative flex h-full w-72 max-w-[85vw] flex-col bg-card shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-5 rounded-lg p-1 text-muted-foreground hover:bg-secondary"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <aside className="hidden h-screen w-64 shrink-0 flex-col border-r border-border bg-card lg:flex">
        {sidebar}
      </aside>
    </>
  );
}
